import { DOCUMENT, Location } from '@angular/common';
import { ɵComponentType as ComponentType } from '@angular/core';
import { distinctUntilFulfilmentChanged, FulfilmentState, FulfilmentStoreService } from '@woolworthsnz/fulfilment';
import { InjectorContainerModule, MutationObserverService, RouterNavigationService } from '@woolworthsnz/styleguide';
import { merge, Subscription } from 'rxjs';
import { debounceTime, skipWhile } from 'rxjs/operators';
import { BannerTarget, DynamicContentService, DynamicContentTargets } from '../services';

export interface DynamicContentOptions {
	target?: string | BannerTarget;
	dynamicContentTarget?: DynamicContentTargets; // If this is specified, wait for the element to be created
	reinitialiseOnRouteChanges?: boolean;
}

/**
 *
 * @param type Accepts either a string or BannerTarget object for loading DC. Due to the various ways DC is previously loaded this tries a few things for loading DC. First, if no params passed in it will look for a getDynamicContentBannerTarget method on the instance (useful for search pages that load the content based on runtime values). Second, if a string is passed to the decorator it will assume it's a url and request for that. Third, if a BannerTarget is passed in it will just request based on that and finally if no paramters exist it will request for the current location pathname
 * @param opt Dynamic Content Options
 */
function setDynamicContentOnInit<T>(type: ComponentType<T>, opt?: DynamicContentOptions): void {
	// Grab the original ngOnInit function
	const originalNgAfterViewInit = type.prototype.ngAfterViewInit;
	const originalNgOnDestroy = type.prototype.ngOnDestroy;

	let dcServiceTimeoutId: ReturnType<typeof setTimeout>;
	let dcServiceSubscription: Subscription;
	let fulfilmentStoreStateSubscription: Subscription;
	let waitForElementSubscription: Subscription;

	type.prototype.ngAfterViewInit = function (args: any) {
		if (originalNgAfterViewInit) {
			originalNgAfterViewInit.apply(this, args);
		}

		const fulfilmentStoreService = InjectorContainerModule.injector.get(FulfilmentStoreService);
		const dynamicContentService = InjectorContainerModule.injector.get(DynamicContentService);
		const location = InjectorContainerModule.injector.get(Location);
		const routerNavigationService = InjectorContainerModule.injector.get(RouterNavigationService);
		const mutationObserverService = InjectorContainerModule.injector.get(MutationObserverService);
		const document = InjectorContainerModule.injector.get(DOCUMENT);

		const fulfilmentObservable = fulfilmentStoreService.state$.pipe(
			distinctUntilFulfilmentChanged(),
			skipWhile((val: FulfilmentState) => !val.updatedFromServer)
		);

		const loadDynamicContent = (): Subscription | void => {
			let arg: BannerTarget;

			if (!opt?.target) {
				if (this.getDynamicContentBannerTarget) {
					arg = this.getDynamicContentBannerTarget();
				} else {
					arg = <BannerTarget>{
						url: decodeURI(location.path(false).split('?')[0]),
					};
				}
			} else if (typeof opt.target === 'string') {
				arg = <BannerTarget>{
					url: opt.target,
				};
			} else {
				arg = opt.target;
			}

			// If the dynamic content is supposed to be loaded into a container element, make sure the container element exists
			// before loading the dynamic content
			if (opt?.dynamicContentTarget) {
				const elementSelector = `#${opt.dynamicContentTarget}`;
				const contentTargetElement = document.body.querySelector(elementSelector);

				// If the container element doesn't exist, then wait for the element to be created before loading the dynamic content
				if (!contentTargetElement) {
					return (waitForElementSubscription = mutationObserverService
						.waitForElement(document.body, elementSelector)
						.subscribe((element) => {
							if (element) {
								dcServiceSubscription = dynamicContentService.init(arg);
							}
						}));
				}
			}

			// In some scenarios, the panel is not ready when initialize the dynamic content.
			// e.g. go to specials, and click nav back to homepage
			// So wait for the next tick to init dynamic content
			dcServiceTimeoutId = setTimeout(() => {
				dcServiceSubscription = dynamicContentService.init(arg);
			});
		};

		if (opt?.reinitialiseOnRouteChanges) {
			fulfilmentStoreStateSubscription = merge(fulfilmentObservable, routerNavigationService.filteredUrl$)
				.pipe(debounceTime(50))
				.subscribe(loadDynamicContent);
		} else {
			fulfilmentStoreStateSubscription = fulfilmentObservable.subscribe(loadDynamicContent);
		}
	};

	type.prototype.ngOnDestroy = function (args: any) {
		if (fulfilmentStoreStateSubscription && !fulfilmentStoreStateSubscription.closed) {
			fulfilmentStoreStateSubscription.unsubscribe();
		}

		if (dcServiceSubscription && !dcServiceSubscription.closed) {
			dcServiceSubscription.unsubscribe();
		}

		if (waitForElementSubscription && !waitForElementSubscription.closed) {
			waitForElementSubscription.unsubscribe();
		}

		if (originalNgOnDestroy) {
			originalNgOnDestroy.apply(this, args);
		}

		if (dcServiceTimeoutId) {
			clearTimeout(dcServiceTimeoutId);
		}
	};
}

/**
 * Class Decorator to handle dynamic content related to the decorated component.
 * Can be passed:
 *  a string (the url of the dynamic content to load)
 *  a BannerTarget - see BannerTarget class
 *  a function that returns a BannerTarget
 *  defaults to calling for current location
 *
 * usage:
 * ```
 * @WithDynamicContent({ target: '/shop/content/somecontent'})
 * @Component({
 *     ...
 * })
 * ```
 * @param opt The string or BannerTarget or callback that returns a BannerTarget
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export function WithDynamicContent(opt?: DynamicContentOptions): ClassDecorator {
	return (type: any) => {
		setDynamicContentOnInit(type, opt);
	};
}
